import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Flex, Text, Switch, Input, Button, useToast, VStack, HStack,
    Badge, Divider, Select, NumberInput, NumberInputField, Textarea,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, IconButton,
    SimpleGrid, Wrap, WrapItem,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton,
    ModalBody, ModalFooter, useDisclosure,
} from '@chakra-ui/react';
import { FaPhoneAlt, FaSyncAlt, FaEye } from 'react-icons/fa';
import { getBotLigacao, updateBotLigacaoConfig } from '../services/api';
import { semBloqueados } from '../hooks/useUsuarios';

const ACCENT = '#F68633';

const DIAS_SEMANA = [
    { v: 1, label: 'Seg' }, { v: 2, label: 'Ter' }, { v: 3, label: 'Qua' },
    { v: 4, label: 'Qui' }, { v: 5, label: 'Sex' }, { v: 6, label: 'Sáb' },
    { v: 0, label: 'Dom' },
];

const VOZ_LABEL = {
    tempo: 'Tempo (masculina, português BR)',
    bossa: 'Bossa (feminina, português BR)',
    marin: 'Marin (feminina, inglês)',
    cedar: 'Cedar (masculina, inglês)',
    meridian: 'Meridian (masculina, inglês)',
    gleam: 'Gleam (feminina, inglês)',
    vesper: 'Vesper (masculina, britânica)',
    willow: 'Willow (feminina, irlandesa)',
    stone: 'Stone (masculina, irlandesa)',
    ripple: 'Ripple (masculina, australiana)',
    quartz: 'Quartz (feminina, australiana)',
};

const STATUS_LABEL = {
    concluida: { cor: 'green', texto: 'concluída' },
    em_andamento: { cor: 'orange', texto: 'em andamento' },
    humano_atendeu: { cor: 'blue', texto: 'humano atendeu' },
    nao_atendida: { cor: 'gray', texto: 'não atendida' },
    orfa: { cor: 'gray', texto: 'órfã' },
    erro: { cor: 'red', texto: 'erro' },
    rejeitada: { cor: 'gray', texto: 'rejeitada' },
};

const gradeDefault = () =>
    [0, 1, 2, 3, 4, 5, 6].map(() => ({ ativo: true, inicio: '08:00', fim: '18:00' }));

const normalizarGrade = (g) => {
    const base = gradeDefault();
    if (Array.isArray(g)) {
        g.forEach((d, i) => {
            if (i < 7 && d && typeof d === 'object') {
                base[i] = {
                    ativo: !!d.ativo,
                    inicio: d.inicio || '08:00',
                    fim: d.fim || '18:00',
                };
            }
        });
    }
    return base;
};

const formatarUsd = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '-';
    return `US$ ${n.toFixed(n < 1 ? 3 : 2)}`;
};

const segundosDesde = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    return Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
};

const BotLigacaoTab = () => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState({
        ativo: false, horario_restrito: false, horario_dias: gradeDefault(),
        system_prompt: '', prompt_backend: '', greeting: '',
        modelo: 'gpt-live-1', backend_modelo: 'gpt-5.6-terra', voz: 'tempo',
        max_duracao_seg: 600, atender_apos_seg: 12, teto_diario_usd: 20,
        gravar_chamadas: true, user_sip_id: '', template_link_tag: 'pos_ligacao_link',
    });
    const [estado, setEstado] = useState({});
    const [padroes, setPadroes] = useState({ system_prompt: '', prompt_backend: '', saudacao: '' });
    const [opcoes, setOpcoes] = useState({ modelos: [], backend_modelos: [], vozes: [], usuarios_sip: [] });
    const [logs, setLogs] = useState([]);
    const [logSelecionado, setLogSelecionado] = useState(null);
    const detalhesDisclosure = useDisclosure();
    const abrirDetalhes = (log) => { setLogSelecionado(log); detalhesDisclosure.onOpen(); };

    const carregar = useCallback(async (com_loading = false, incluirConfig = false) => {
        if (com_loading) setLoading(true);
        try {
            const { data } = await getBotLigacao();
            if (incluirConfig) {
                const cfg = data.config || {};
                setConfig({
                    ativo: !!cfg.ativo,
                    horario_restrito: !!cfg.horario_restrito,
                    horario_dias: normalizarGrade(cfg.horario_dias),
                    system_prompt: cfg.system_prompt || '',
                    prompt_backend: cfg.prompt_backend || '',
                    greeting: cfg.greeting || '',
                    modelo: cfg.modelo || 'gpt-live-1',
                    backend_modelo: cfg.backend_modelo || 'gpt-5.6-terra',
                    voz: cfg.voz || 'tempo',
                    max_duracao_seg: cfg.max_duracao_seg || 600,
                    atender_apos_seg: cfg.atender_apos_seg ?? 12,
                    teto_diario_usd: Number(cfg.teto_diario_usd ?? 20),
                    gravar_chamadas: cfg.gravar_chamadas !== false,
                    user_sip_id: cfg.user_sip_id || '',
                    template_link_tag: cfg.template_link_tag || 'pos_ligacao_link',
                });
                setPadroes({
                    system_prompt: data.system_prompt_padrao || '',
                    prompt_backend: data.prompt_backend_padrao || '',
                    saudacao: data.saudacao_padrao || '',
                });
                setOpcoes(data.opcoes || { modelos: [], backend_modelos: [], vozes: [], usuarios_sip: [] });
            }
            setEstado(data.estado || {});
            setLogs(data.logs || []);
        } catch (e) {
            toast({ title: 'Erro ao carregar configuração do bot de ligação.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            if (com_loading) setLoading(false);
        }
    }, [toast]);

    useEffect(() => {
        carregar(true, true);
        const t = setInterval(() => carregar(false), 20000);
        return () => clearInterval(t);
    }, [carregar]);

    const salvar = async (overrides, mensagem) => {
        setSaving(true);
        try {
            const payload = {
                ativo: config.ativo,
                horario_restrito: config.horario_restrito,
                horario_dias: config.horario_dias,
                greeting: config.greeting || null,
                modelo: config.modelo,
                backend_modelo: config.backend_modelo,
                voz: config.voz,
                max_duracao_seg: Number(config.max_duracao_seg) || 600,
                atender_apos_seg: Number(config.atender_apos_seg) || 0,
                teto_diario_usd: Number(config.teto_diario_usd) || 0,
                gravar_chamadas: config.gravar_chamadas,
                user_sip_id: config.user_sip_id || null,
                template_link_tag: config.template_link_tag || null,
                ...overrides,
            };
            const { data } = await updateBotLigacaoConfig(payload);
            setConfig((c) => ({
                ...c,
                ...data,
                horario_dias: normalizarGrade(data?.horario_dias),
                system_prompt: data?.system_prompt || '',
                prompt_backend: data?.prompt_backend || '',
                greeting: data?.greeting || '',
                user_sip_id: data?.user_sip_id || '',
                teto_diario_usd: Number(data?.teto_diario_usd ?? 0),
            }));
            if (mensagem) {
                toast({ title: mensagem, status: 'success', duration: 3000, isClosable: true });
            }
            carregar(false);
        } catch (e) {
            const detail = e?.response?.data?.message || 'Erro ao salvar.';
            toast({ title: detail, status: 'error', duration: 4000, isClosable: true });
        } finally {
            setSaving(false);
        }
    };

    const toggleBot = (ativo) => salvar({ ativo }, ativo ? 'Bot de ligação ativado.' : 'Bot de ligação desativado.');
    const toggleHorarioRestrito = (restrito) => salvar({ horario_restrito: restrito },
        restrito ? 'Atendimento por horário ativado.' : 'Atendimento por horário desativado.');
    const setDia = (diaNum, patch) => {
        setConfig((c) => {
            const grade = normalizarGrade(c.horario_dias);
            grade[diaNum] = { ...grade[diaNum], ...patch };
            return { ...c, horario_dias: grade };
        });
    };

    const formatarData = (iso) => {
        if (!iso) return '-';
        const d = new Date(String(iso).replace(' ', 'T'));
        if (isNaN(d.getTime())) return '-';
        return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    const formatarDuracao = (seg) => {
        if (!seg) return '-';
        if (seg < 60) return `${seg}s`;
        const m = Math.floor(seg / 60);
        const s = seg % 60;
        return `${m}min ${s}s`;
    };

    const statusBadge = (l) => {
        const s = STATUS_LABEL[l.status] || { cor: 'orange', texto: l.status };
        const extra = l.motivo_encerramento && l.motivo_encerramento !== l.status ? ` (${l.motivo_encerramento.replace(/_/g, ' ')})` : '';
        return <Badge colorScheme={s.cor}>{s.texto}{extra}</Badge>;
    };

    if (loading) {
        return (
            <Flex justify="center" py={10}>
                <Text color="gray.500">Carregando...</Text>
            </Flex>
        );
    }

    const estadoBadge = !config.ativo
        ? { cor: 'gray', texto: 'DESLIGADO' }
        : estado.motivo_parado === 'fora_do_horario'
            ? { cor: 'yellow', texto: 'FORA DO HORÁRIO' }
            : estado.motivo_parado === 'teto_diario'
                ? { cor: 'red', texto: 'TETO DIÁRIO ATINGIDO' }
                : { cor: 'green', texto: 'ATIVO AGORA' };
    const workerVistoHa = segundosDesde(estado.worker_visto_em);

    return (
        <VStack spacing={5} align="stretch">
            {/* Status */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Flex align="center" justify="space-between" wrap="wrap" gap={4}>
                    <Box flex="1" minW={{ base: 'full', md: '260px' }}>
                        <HStack mb={1}>
                            <FaPhoneAlt color={ACCENT} />
                            <Text fontWeight="bold" fontSize="lg">Atendente IA por voz</Text>
                        </HStack>
                        <Text fontSize="sm" color="gray.500">
                            Quando ligado, o ramal configurado é atendido pela IA (GPT-Live 1). Se algum atendente estiver com o softphone aberto, a IA espera o tempo configurado antes de atender; se ele pegar a ligação, a IA fica quieta.
                        </Text>
                    </Box>
                    <HStack spacing={3}>
                        <Badge colorScheme={estadoBadge.cor} fontSize="0.8em" px={2} py={1} borderRadius="md">
                            {estadoBadge.texto}
                        </Badge>
                        <Switch
                            size="lg" colorScheme="orange"
                            isChecked={!!config.ativo}
                            isDisabled={saving}
                            onChange={(e) => toggleBot(e.target.checked)}
                        />
                    </HStack>
                </Flex>

                <Wrap mt={4} spacing={2}>
                    <WrapItem>
                        <Badge colorScheme={estado.worker_online ? 'green' : 'red'} variant="subtle" px={2} py={1} borderRadius="md">
                            Worker {estado.worker_online ? `online (visto há ${workerVistoHa ?? '?'}s)` : 'offline'}
                        </Badge>
                    </WrapItem>
                    <WrapItem>
                        <Badge colorScheme={estado.worker_registrado ? 'green' : 'gray'} variant="subtle" px={2} py={1} borderRadius="md">
                            Ramal {estado.worker_registrado ? 'registrado' : 'não registrado'}
                        </Badge>
                    </WrapItem>
                    {estado.worker_em_chamada && (
                        <WrapItem>
                            <Badge colorScheme="orange" variant="subtle" px={2} py={1} borderRadius="md">Em chamada agora</Badge>
                        </WrapItem>
                    )}
                    <WrapItem>
                        <Badge colorScheme={estado.humanos_online ? 'blue' : 'gray'} variant="subtle" px={2} py={1} borderRadius="md">
                            {estado.humanos_online
                                ? `Atendentes online: ${(estado.humanos_online_nomes || []).join(', ')}`
                                : 'Nenhum atendente com softphone aberto'}
                        </Badge>
                    </WrapItem>
                    <WrapItem>
                        <Badge colorScheme={estado.motivo_parado === 'teto_diario' ? 'red' : 'purple'} variant="subtle" px={2} py={1} borderRadius="md">
                            Custo hoje {formatarUsd(estado.custo_hoje_usd)} de {formatarUsd(estado.teto_diario_usd)}
                        </Badge>
                    </WrapItem>
                    {estado.worker_versao && (
                        <WrapItem>
                            <Badge colorScheme="gray" variant="subtle" px={2} py={1} borderRadius="md">v{estado.worker_versao}</Badge>
                        </WrapItem>
                    )}
                </Wrap>

                <Divider my={4} />

                <Flex align="center" justify="space-between" wrap="wrap" gap={3}>
                    <Box>
                        <Text fontWeight="bold" fontSize="md">Horário de funcionamento</Text>
                        <Text fontSize="sm" color="gray.500">
                            Fora do horário o ramal da IA fica desregistrado (a ligação segue o roteamento normal da NVOIP).
                        </Text>
                    </Box>
                    <Switch
                        colorScheme="orange"
                        isChecked={!!config.horario_restrito}
                        isDisabled={saving}
                        onChange={(e) => toggleHorarioRestrito(e.target.checked)}
                    />
                </Flex>

                {config.horario_restrito && (
                    <VStack align="stretch" spacing={2} mt={3}>
                        {DIAS_SEMANA.map((d) => {
                            const dia = (config.horario_dias || [])[d.v] || { ativo: false, inicio: '08:00', fim: '18:00' };
                            return (
                                <Flex
                                    key={d.v} align="center" gap={3} wrap="wrap"
                                    py={2} px={3} borderRadius="md"
                                    bg={dia.ativo ? 'white' : 'gray.50'}
                                    border="1px solid" borderColor="gray.200"
                                >
                                    <Text w="42px" fontWeight="medium" fontSize="sm">{d.label}</Text>
                                    <Switch
                                        size="sm" colorScheme="orange"
                                        isChecked={dia.ativo}
                                        isDisabled={saving}
                                        onChange={(e) => setDia(d.v, { ativo: e.target.checked })}
                                    />
                                    {dia.ativo ? (
                                        <>
                                            <Input
                                                type="time" size="sm" maxW="120px" bg="white"
                                                value={dia.inicio || '08:00'}
                                                onChange={(e) => setDia(d.v, { inicio: e.target.value })}
                                            />
                                            <Text fontSize="sm">às</Text>
                                            <Input
                                                type="time" size="sm" maxW="120px" bg="white"
                                                value={dia.fim || '18:00'}
                                                onChange={(e) => setDia(d.v, { fim: e.target.value })}
                                            />
                                        </>
                                    ) : (
                                        <Text fontSize="sm" color="gray.400">não atende</Text>
                                    )}
                                </Flex>
                            );
                        })}
                        <Flex pt={2}>
                            <Button colorScheme="orange" size="sm" onClick={() => salvar({}, 'Horário salvo.')} isLoading={saving}>
                                Salvar horário
                            </Button>
                        </Flex>
                    </VStack>
                )}
            </Box>

            {/* Ramal SIP + espera antes de atender */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={1}>Ramal SIP da IA</Text>
                <Text fontSize="sm" color="gray.600" mb={3}>
                    {estado.modo_pbx
                        ? `A IA tem ramal próprio (${estado.ramal_bot}) no PABX da TAGA. Ela não divide mais o ramal com ninguém, e quem decide a espera é o PABX.`
                        : 'O worker registra na NVOIP como este usuário e atende o que tocar nesse ramal. Enquanto a IA estiver ligada, ninguém deve usar o softphone com esse usuário.'}
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>{estado.modo_pbx ? 'Ramal da IA' : 'Usuário SIP'}</Text>
                        {estado.modo_pbx ? (
                            <>
                                <Input size="sm" bg="gray.50" value={estado.ramal_bot || ''} isReadOnly />
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    Definido pelo PABX (PBX_RAMAL_BOT). Não divide registro com atendente.
                                </Text>
                            </>
                        ) : (
                            <>
                                <Select size="sm" bg="white" placeholder="— Selecione —" value={config.user_sip_id || ''}
                                    onChange={(e) => setConfig((c) => ({ ...c, user_sip_id: e.target.value }))}>
                                    {semBloqueados(opcoes.usuarios_sip || [], config.user_sip_id).map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
                                </Select>
                                <Text fontSize="xs" color="gray.500" mt={1}>
                                    Só aparecem usuários com NumberSIP e senha cadastrados no perfil.
                                </Text>
                            </>
                        )}
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>{estado.modo_pbx ? 'Tocar nos ramais antes (segundos)' : 'Atender após (segundos)'}</Text>
                        <NumberInput size="sm" min={0} max={60} value={config.atender_apos_seg}
                            onChange={(v) => setConfig((c) => ({ ...c, atender_apos_seg: Number(v) || 0 }))}>
                            <NumberInputField bg="white" />
                        </NumberInput>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                            {estado.modo_pbx
                                ? 'Tempo que a ligação toca em TODOS os ramais conectados antes da IA assumir. Ninguém conectado, a IA atende na hora.'
                                : 'Tempo que a ligação fica tocando para um atendente com softphone aberto antes da IA atender. Sem ninguém online, a IA atende na hora.'}
                        </Text>
                    </Box>
                </SimpleGrid>
                <Flex pt={3}>
                    <Button colorScheme="orange" size="sm" isLoading={saving}
                        onClick={() => (estado.modo_pbx
                            ? salvar({}, 'Tempo de toque salvo.')
                            : salvar({ user_sip_id: config.user_sip_id || null }, 'Ramal salvo.'))}>
                        {estado.modo_pbx ? 'Salvar tempo de toque' : 'Salvar ramal'}
                    </Button>
                </Flex>
            </Box>

            {/* Modelo, voz e limites */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={1}>Modelo, voz e limites</Text>
                <Text fontSize="sm" color="gray.600" mb={3}>
                    A voz é o <code>gpt-live-1</code> (US$ 0,05 por minuto, cobrado por segundo). Quem raciocina e usa as ferramentas é o modelo de backend, cobrado por token: <code>gpt-6-sol</code> é o recomendado (responde o preço na metade do tempo do <code>gpt-5.6-terra</code>, pelo mesmo custo); <code>luna</code> é o mais barato.
                </Text>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Modelo de voz</Text>
                        <Select size="sm" bg="white" value={config.modelo} onChange={(e) => setConfig((c) => ({ ...c, modelo: e.target.value }))}>
                            {(opcoes.modelos || []).map((m) => <option key={m} value={m}>{m}</option>)}
                        </Select>
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Modelo de backend (regras e ferramentas)</Text>
                        <Select size="sm" bg="white" value={config.backend_modelo} onChange={(e) => setConfig((c) => ({ ...c, backend_modelo: e.target.value }))}>
                            {(opcoes.backend_modelos || []).map((m) => <option key={m} value={m}>{m}</option>)}
                        </Select>
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Voz</Text>
                        <Select size="sm" bg="white" value={config.voz} onChange={(e) => setConfig((c) => ({ ...c, voz: e.target.value }))}>
                            {(opcoes.vozes || []).map((v) => <option key={v} value={v}>{VOZ_LABEL[v] || v}</option>)}
                        </Select>
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Duração máxima (s)</Text>
                        <NumberInput size="sm" min={60} max={3600} value={config.max_duracao_seg}
                            onChange={(v) => setConfig((c) => ({ ...c, max_duracao_seg: Number(v) || 600 }))}>
                            <NumberInputField bg="white" />
                        </NumberInput>
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Teto diário (US$)</Text>
                        <NumberInput size="sm" min={0} max={1000} step={1} value={config.teto_diario_usd}
                            onChange={(v) => setConfig((c) => ({ ...c, teto_diario_usd: Number(v) || 0 }))}>
                            <NumberInputField bg="white" />
                        </NumberInput>
                        <Text fontSize="xs" color="gray.500" mt={1}>Atingido o teto, a IA para de atender até o dia seguinte. 0 = sem teto.</Text>
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Template WhatsApp do link (tag na Poli)</Text>
                        <Input size="sm" bg="white" value={config.template_link_tag || ''}
                            onChange={(e) => setConfig((c) => ({ ...c, template_link_tag: e.target.value }))} />
                        <Text fontSize="xs" color="gray.500" mt={1}>Template aprovado pela Meta com uma variável (o link). Usado quando o cliente fecha o pedido por telefone.</Text>
                    </Box>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Gravação</Text>
                        <HStack pt={1}>
                            <Switch
                                colorScheme="orange"
                                isChecked={!!config.gravar_chamadas}
                                onChange={(e) => setConfig((c) => ({ ...c, gravar_chamadas: e.target.checked }))}
                            />
                            <Text fontSize="sm">Gravar áudio (30 dias na OpenAI)</Text>
                        </HStack>
                        <Text fontSize="xs" color="gray.500" mt={1}>A transcrição é salva sempre. A gravação de áudio depende do projeto da OpenAI permitir armazenamento; quando ela está de fato ativa, a IA avisa "essa ligação é gravada" na saudação.</Text>
                    </Box>
                </SimpleGrid>
                <Flex pt={3}>
                    <Button colorScheme="orange" size="sm" onClick={() => salvar({}, 'Parâmetros salvos.')} isLoading={saving}>
                        Salvar parâmetros
                    </Button>
                </Flex>
            </Box>

            {/* Saudação */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={1}>Saudação</Text>
                <Text fontSize="sm" color="gray.600" mb={3}>
                    A IA fala isso assim que atende, antes do cliente falar. Deixe em branco para usar a padrão. Diga sempre que é um assistente virtual.
                </Text>
                <Textarea
                    value={config.greeting || ''}
                    onChange={(e) => setConfig((c) => ({ ...c, greeting: e.target.value }))}
                    rows={2}
                    fontSize="sm"
                    placeholder={padroes.saudacao || 'TAGA Autopeças, aqui é o Carlos, assistente virtual. Me diz a peça e o veículo que você precisa.'}
                />
                <Flex pt={3}>
                    <Button colorScheme="orange" size="sm" onClick={() => salvar({ greeting: config.greeting || null }, 'Saudação salva.')} isLoading={saving}>
                        Salvar saudação
                    </Button>
                </Flex>
            </Box>

            {/* Prompt de conversa */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={1}>Conversa (modelo de voz)</Text>
                <Text fontSize="sm" color="gray.600" mb={3}>
                    Papel, tom, quando interromper e quando pedir ajuda ao backend. Curto de propósito. Regra de negócio vai no prompt de baixo. Quando vazio, usa o padrão.
                </Text>
                <Textarea
                    value={config.system_prompt || ''}
                    onChange={(e) => setConfig((c) => ({ ...c, system_prompt: e.target.value }))}
                    minH={{ base: '220px', md: '320px' }}
                    fontSize="sm"
                    fontFamily="monospace"
                />
                <Flex pt={3} gap={3} wrap="wrap">
                    <Button colorScheme="orange" size="sm" isLoading={saving}
                        onClick={() => salvar({ system_prompt: config.system_prompt || null }, config.system_prompt ? 'Prompt de conversa salvo.' : 'Prompt limpo — voltou ao padrão.')}>
                        Salvar prompt de conversa
                    </Button>
                    <Button
                        size="sm" variant="outline"
                        onClick={() => setConfig((c) => ({ ...c, system_prompt: padroes.system_prompt }))}
                        isDisabled={!padroes.system_prompt || config.system_prompt === padroes.system_prompt}
                    >
                        Restaurar padrão
                    </Button>
                </Flex>
            </Box>

            {/* Prompt do backend */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Text fontWeight="bold" fontSize="lg" mb={1}>Regras de negócio (backend)</Text>
                <Text fontSize="sm" color="gray.600" mb={3}>
                    O que a TAGA vende e como, como buscar, o que nunca prometer, quando registrar recado e como devolver o resultado para a voz. Quando vazio, usa o padrão.
                </Text>
                <Textarea
                    value={config.prompt_backend || ''}
                    onChange={(e) => setConfig((c) => ({ ...c, prompt_backend: e.target.value }))}
                    minH={{ base: '260px', md: '420px' }}
                    fontSize="sm"
                    fontFamily="monospace"
                />
                <Flex pt={3} gap={3} wrap="wrap">
                    <Button colorScheme="orange" size="sm" isLoading={saving}
                        onClick={() => salvar({ prompt_backend: config.prompt_backend || null }, config.prompt_backend ? 'Regras salvas.' : 'Regras limpas — voltou ao padrão.')}>
                        Salvar regras
                    </Button>
                    <Button
                        size="sm" variant="outline"
                        onClick={() => setConfig((c) => ({ ...c, prompt_backend: padroes.prompt_backend }))}
                        isDisabled={!padroes.prompt_backend || config.prompt_backend === padroes.prompt_backend}
                    >
                        Restaurar padrão
                    </Button>
                </Flex>
            </Box>

            {/* Modal de detalhes da chamada */}
            <Modal
                isOpen={detalhesDisclosure.isOpen} onClose={detalhesDisclosure.onClose}
                size={{ base: 'full', md: '3xl' }} scrollBehavior="inside"
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">
                        Chamada {logSelecionado?.id} · {logSelecionado?.telefone || 'sem telefone'} · {formatarDuracao(logSelecionado?.duracao_seg)}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={6}>
                        <Wrap spacing={2} mb={3}>
                            <WrapItem>{logSelecionado && statusBadge(logSelecionado)}</WrapItem>
                            <WrapItem><Badge colorScheme="purple" variant="subtle">Custo {formatarUsd(logSelecionado?.custo_usd)}</Badge></WrapItem>
                            {logSelecionado?.duracao_voz_seg != null && (
                                <WrapItem><Badge variant="subtle">Voz {formatarDuracao(logSelecionado.duracao_voz_seg)}</Badge></WrapItem>
                            )}
                            {logSelecionado?.backend_modelo && (
                                <WrapItem><Badge variant="subtle">{logSelecionado.backend_modelo} · {logSelecionado.tokens_backend_input || 0} in / {logSelecionado.tokens_backend_output || 0} out</Badge></WrapItem>
                            )}
                            {logSelecionado?.atendida_apos_seg != null && (
                                <WrapItem><Badge variant="subtle">atendida após {logSelecionado.atendida_apos_seg}s</Badge></WrapItem>
                            )}
                        </Wrap>
                        <Text fontSize="xs" color="gray.500" mb={3}>
                            Iniciada: {formatarData(logSelecionado?.iniciada_em)}
                            {logSelecionado?.encerrada_em && ` · Encerrada: ${formatarData(logSelecionado.encerrada_em)}`}
                            {logSelecionado?.openai_session_id && ` · sessão ${logSelecionado.openai_session_id}`}
                        </Text>
                        <Text fontWeight="bold" fontSize="sm" mb={2}>Transcrição</Text>
                        <Box bg="gray.50" p={3} borderRadius="md" maxH="50vh" overflowY="auto" mb={4}>
                            {logSelecionado?.transcricao
                                ? logSelecionado.transcricao.split('\n').map((linha, idx) => {
                                    const quem = linha.startsWith('CLIENTE:') ? 'cliente'
                                        : linha.startsWith('IA:') ? 'ia' : null;
                                    if (!quem) return <Text key={idx} fontSize="sm" color="gray.500">{linha}</Text>;
                                    const texto = linha.replace(/^(CLIENTE|IA):\s*/, '');
                                    return (
                                        <Flex key={idx} mb={2} gap={2}>
                                            <Badge colorScheme={quem === 'cliente' ? 'blue' : 'orange'} fontSize="10px" minW="60px" textAlign="center">
                                                {quem === 'cliente' ? 'Cliente' : 'Bot'}
                                            </Badge>
                                            <Text fontSize="sm" color="gray.800" flex="1">{texto}</Text>
                                        </Flex>
                                    );
                                })
                                : <Text fontSize="sm" color="gray.400">(sem transcrição)</Text>}
                        </Box>

                        {Array.isArray(logSelecionado?.function_calls) && logSelecionado.function_calls.length > 0 && (
                            <>
                                <Text fontWeight="bold" fontSize="sm" mb={2}>Ferramentas ({logSelecionado.function_calls.length})</Text>
                                <VStack align="stretch" spacing={2}>
                                    {logSelecionado.function_calls.map((fc, idx) => (
                                        <Box key={idx} bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={2}>
                                            <HStack mb={1}>
                                                <Badge colorScheme="purple">{fc.name}</Badge>
                                            </HStack>
                                            <Text fontSize="xs" color="gray.600" fontFamily="monospace" whiteSpace="pre-wrap">
                                                args: {JSON.stringify(fc.args, null, 2)}
                                            </Text>
                                            <Text fontSize="xs" color="gray.600" fontFamily="monospace" whiteSpace="pre-wrap" mt={1}>
                                                result: {JSON.stringify(fc.result, null, 2).slice(0, 600)}
                                                {JSON.stringify(fc.result).length > 600 ? '...' : ''}
                                            </Text>
                                        </Box>
                                    ))}
                                </VStack>
                            </>
                        )}

                        {logSelecionado?.erro && (
                            <Box mt={3} bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" p={2}>
                                <Text fontSize="xs" color="red.700" fontWeight="bold">Erro</Text>
                                <Text fontSize="xs" color="red.700" fontFamily="monospace">{logSelecionado.erro}</Text>
                            </Box>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button size="sm" onClick={detalhesDisclosure.onClose}>Fechar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Logs */}
            <Box bg="white" boxShadow="md" borderRadius="lg" p={{ base: 4, md: 6 }}>
                <Flex align="center" justify="space-between" mb={3}>
                    <Text fontWeight="bold" fontSize="lg">Chamadas recentes</Text>
                    <IconButton aria-label="Atualizar" icon={<FaSyncAlt />} size="sm" variant="ghost" onClick={() => carregar(false)} />
                </Flex>
                {logs.length === 0 ? (
                    <Text fontSize="sm" color="gray.400" textAlign="center" py={4}>
                        Nenhuma chamada registrada ainda.
                    </Text>
                ) : (
                    <TableContainer>
                        <Table size="sm" variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>Quando</Th>
                                    <Th>Telefone</Th>
                                    <Th>Status</Th>
                                    <Th>Duração</Th>
                                    <Th>Ferr.</Th>
                                    <Th isNumeric>Custo</Th>
                                    <Th>Detalhes</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {logs.map((l) => {
                                    const qtdTools = Array.isArray(l.function_calls) ? l.function_calls.length : 0;
                                    return (
                                        <Tr key={l.id} _hover={{ bg: 'gray.50' }}>
                                            <Td whiteSpace="nowrap" fontSize="xs">{formatarData(l.iniciada_em || l.created_at)}</Td>
                                            <Td fontSize="xs">{l.telefone || '-'}</Td>
                                            <Td>{statusBadge(l)}</Td>
                                            <Td fontSize="xs">{formatarDuracao(l.duracao_seg)}</Td>
                                            <Td fontSize="xs">{qtdTools > 0 ? `${qtdTools}×` : '-'}</Td>
                                            <Td fontSize="xs" isNumeric>{l.custo_usd != null ? formatarUsd(l.custo_usd) : '-'}</Td>
                                            <Td>
                                                <IconButton
                                                    aria-label="Ver transcrição"
                                                    icon={<FaEye />}
                                                    size="xs"
                                                    variant="ghost"
                                                    colorScheme="orange"
                                                    onClick={() => abrirDetalhes(l)}
                                                />
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </VStack>
    );
};

export default BotLigacaoTab;
